import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, CheckCircle } from 'lucide-react';
import { supabase } from '../../../../src/lib/supabase';
import { logNegotiationAction } from '../../../../src/lib/negotiationLogger';
import { checkAndRequestDiscountApproval } from '../../../../src/lib/discountApproval';
import { calcularMultiplicadorFinal, isFidelidadeIsenta, modalidadePorPerfil, podeEscolherModalidadeNaCriacao, type ModalidadePlano } from '../../../../src/lib/politicaComercial';
import { ehComercialInterno } from '../../../../src/lib/departamentos';
import { funilDeVendas, buscarFunilDeVendasId } from '../../../../src/lib/pipelines';
import { sanitizeNomeParaArquivo } from '../../../../src/lib/utils';
import { useToast } from '../../../../src/contexts/ToastContext';
import { uploadDocumentsToNegotiation } from '../documentUpload';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { logUsageEvent } from '../../../../src/lib/logger';
import { buscarUcComMesmoNumero, mensagemUcDuplicada, numeroRepetidoNoLote } from '../../../../src/lib/ucDuplicada';
import { solicitarTrocaDonoUc } from '../../../../src/lib/trocaDonoUc';
import { resolverPagador } from '../../../../src/services/pagadores';
import StepClientType from './StepClientType';
import StepBasicInfo from './StepBasicInfo';
import StepUC, { UCData } from './StepUC';
import StepPlan from './StepPlan';
import StepDocuments from './StepDocuments';

interface NegotiationWizardProps {
    isOpen: boolean;
    onClose: () => void;
    /** `pipelineId` é o funil onde a negociação realmente caiu — quem chama usa
     *  para levar o quadro até lá, senão a negociação nasce fora da vista. */
    onSuccess: (negociacaoId: number, pipelineId: string | null) => void;
    prefillData?: {
        nome_cliente?: string;
        whatsapp?: string;
        cpf_cnpj?: string;
        /** Já vem definido pelo fluxo "Automático" — pula a etapa manual de escolha PF/PJ. */
        tipoCliente?: 'PF' | 'PJ';
        associateUC?: boolean;
        /** Uma ou mais UCs já lidas de faturas — entram direto na lista de UCs já adicionadas (uma por fatura enviada no laço "Adicionar Nova Fatura"). */
        ucsPrefill?: Partial<UCData>[];
        /** PDFs das faturas usadas para preencher os dados acima — todos anexados aos documentos da negociação após criá-la. */
        faturaFiles?: { file: File; label?: string }[];
        /** Documento cadastral (RG/CNH/comprovante) usado para preencher os dados acima — anexado aos documentos da negociação após criá-la. */
        documentoFile?: File;
        /** Rótulo do documento (ex.: "RG", "Comprovante de Residência") usado como nome do anexo. */
        documentoLabel?: string;
    };
}

const NegotiationWizard: React.FC<NegotiationWizardProps> = ({ isOpen, onClose, onSuccess, prefillData }) => {
    const { addToast } = useToast();
    const { profile } = useAuth();
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [loading, setLoading] = useState(false);

    // Vendedor interno = departamento Comercial Interno ('Vendas Internas' é alias
    // legado — ver ehComercialInterno, fonte única). Pra esses usuários a Origem
    // do lead é obrigatória, e a negociação nasce na modalidade de plano por
    // desconto direto (ver plano_modalidade, carimbado abaixo em handleFinish).
    const isVendedorInterno = ehComercialInterno(profile);
    // Gerente comercial sem função escolhe o modelo do plano (o de quem vai fechar);
    // começa no de Closer e BDR. Os demais seguem o próprio modelo.
    const podeEscolherModalidade = podeEscolherModalidadeNaCriacao(profile);
    const [modalidadeEscolhida, setModalidadeEscolhida] = useState<ModalidadePlano | null>(null);
    const modalidadeNaCriacao: ModalidadePlano = modalidadeEscolhida ?? modalidadePorPerfil(profile);

    // Data State
    const [tipoCliente, setTipoCliente] = useState<'PF' | 'PJ'>('PF');
    const [basicInfo, setBasicInfo] = useState({
        nome_cliente: '',
        whatsapp: '',
        cpf_cnpj: '',
        email: '',
        origem: '',
        plataforma: '',
        campanha: '',
        anuncio: '',
        formulario: '',
        reside_goias: null as boolean | null,
        associateUC: false,
        wantDocuments: false,
        status: 'em_andamento',
        stage: '',
        cpf_representante: '',
        nome_representante_legal: '',
        data_nascimento_representante: '',
        contato_financeiro: '',
        email_financeiro: '',
        whatsapp_financeiro: '',
        contrato_social_data_versao: '',
        // "O assinante também será o pagador?" — null = não respondido.
        pagador_mesmo: null as boolean | null,
        pagador_nome: '',
        pagador_cpf_cnpj: '',
        pagador_email: '',
        pagador_whatsapp: '',
    });
    const [ucs, setUcs] = useState<any[]>([]);
    const [wantPlan, setWantPlan] = useState(false);
    const [documentFiles, setDocumentFiles] = useState<{
        rg_cnh_frente: File | null;
        rg_cnh_verso: File | null;
        conta_luz: File[];
        doc_vinculo: File | null;
        procuracao: File | null;
    }>({
        rg_cnh_frente: null,
        rg_cnh_verso: null,
        conta_luz: [],
        doc_vinculo: null,
        procuracao: null
    });
    const [webhookUrl, setWebhookUrl] = useState<string | null>(null);
    const [planDetails, setPlanDetails] = useState({
        // Plano oficial da política comercial (22% + 50% bandeira) — 20/50 não existe na política nova
        desconto_tarifa: 22,
        desconto_bandeira: 50,
        plano_codigo: '22_50_bandeira',
        // Padrão comercial: sem carência (carência exige aprovação do gerente/admin)
        carencia: 'sem carência',
        fidelidade: '12 meses',
        desconto_adicional: ''
    });

    // Pipeline State
    const [pipelines, setPipelines] = useState<any[]>([]);
    const [pipelineStages, setPipelineStages] = useState<any[]>([]);
    const [selectedPipelineId, setSelectedPipelineId] = useState<string | null>(null);

    React.useEffect(() => {
        if (isOpen) {
            if (isOpen) {
                fetchPipelines().catch(err => console.error('Error fetching pipelines:', err));
                fetchWebhookUrl().catch(err => console.error('Error fetching webhook:', err));
                // Fluxo "Automático" já traz o tipo de cliente definido — pula a etapa manual PF/PJ
                setCurrentStepIndex(prefillData?.tipoCliente ? 1 : 0);
                // Apply prefill data if provided
                if (prefillData) {
                    if (prefillData.tipoCliente) setTipoCliente(prefillData.tipoCliente);
                    setBasicInfo(prev => ({
                        ...prev,
                        nome_cliente: prefillData.nome_cliente || prev.nome_cliente,
                        whatsapp: prefillData.whatsapp || prev.whatsapp,
                        cpf_cnpj: prefillData.cpf_cnpj || prev.cpf_cnpj,
                        associateUC: prefillData.associateUC ?? prev.associateUC,
                    }));
                    // Cada fatura lida no fluxo "Automático" já entra como uma UC "adicionada" (não apenas prefill do formulário)
                    if (prefillData.ucsPrefill && prefillData.ucsPrefill.length > 0) {
                        setUcs(prefillData.ucsPrefill.map(uc => ({
                            id: Math.random().toString(36).substr(2, 9),
                            numero_uc: '',
                            cep: '',
                            logradouro: '',
                            numero: '',
                            complemento: '',
                            quadra: '',
                            lote: '',
                            bairro: '',
                            cidade: '',
                            estado: '',
                            ...uc,
                        })));
                    }
                }
            }
        }
    }, [isOpen]);

    React.useEffect(() => {
        if (selectedPipelineId) {
            fetchPipelineStages(selectedPipelineId);
        }
    }, [selectedPipelineId]);

    const fetchPipelines = async () => {
        const { data } = await supabase
            .from('pipelines')
            .select('*')
            .order('created_at', { ascending: true })
            .order('id', { ascending: true });
        if (data && data.length > 0) {
            setPipelines(data);
            // Venda nova pertence ao Funil de Vendas. Antes era data[0], e como os dois
            // funis têm created_at idêntico a escolha saía no sorteio — foi assim que
            // negociação de vendedor foi parar no Pós-Venda, sumindo do quadro dele.
            setSelectedPipelineId(funilDeVendas(data)?.id ?? data[0].id);
        }
    };

    const fetchPipelineStages = async (pipelineId: string) => {
        const { data } = await supabase.from('pipeline_stages').select('*').eq('pipeline_id', pipelineId).order('position', { ascending: true });
        if (data && data.length > 0) {
            setPipelineStages(data);
            // Update basic info stage to first stage
            setBasicInfo(prev => ({ ...prev, stage: data[0].name }));
        }
    };

    const fetchWebhookUrl = async () => {
        const { data } = await supabase.from('system_settings').select('value').eq('key', 'n8n_negotiation_webhook_url').single();
        if (data) setWebhookUrl(data.value);
    };

    // --- LOGIC: Dynamic Steps ---
    const steps = useMemo(() => {
        const _steps = ['client_type', 'basic_info'];
        if (basicInfo.associateUC) _steps.push('uc');
        if (basicInfo.associateUC && wantPlan) _steps.push('plan');
        if (basicInfo.wantDocuments) _steps.push('documents');
        return _steps;
    }, [basicInfo.associateUC, wantPlan, basicInfo.wantDocuments]);

    const totalSteps = steps.length;
    const currentStepString = steps[currentStepIndex];

    const handleNext = () => {
        if (currentStepIndex < totalSteps - 1) {
            setCurrentStepIndex(currentStepIndex + 1);
        } else {
            handleCreateNegotiation();
        }
    };

    const handleBack = () => {
        if (currentStepIndex > 0) {
            setCurrentStepIndex(currentStepIndex - 1);
        }
    };

    // ── Diagnóstico da criação ──────────────────────────────────────────────
    // Toda falha caía num único toast genérico ("Erro ao criar negociação"), que
    // descartava a causa: o vendedor relatava "deu erro" e não havia o que
    // investigar depois — se o INSERT nem chega a sair do aparelho, também não
    // sobra rastro no servidor. Cada etapa abaixo se identifica no erro, e campo
    // obrigatório em branco é apontado pelo nome antes de qualquer ida ao banco.

    /** Erro do PostgREST/Postgres ou falha de rede, em texto que o vendedor entende. */
    const descreverFalha = (etapa: string, e: any): string => {
        const msg = e?.message || '';
        if (/failed to fetch|load failed|networkerror|network request failed/i.test(msg)) {
            // Sem afirmar o que foi ou não salvo: depende da etapa, e quem chama é
            // que sabe (o aviso do webhook, por exemplo, vem com a negociação já criada).
            return `${etapa}: sem conexão com o servidor — a requisição não chegou a sair do aparelho. Verifique a internet.`;
        }
        const codigo = e?.code ? ` [${e.code}]` : '';
        const detalhe = e?.details ? ` — ${e.details}` : '';
        const dica = e?.hint ? ` (${e.hint})` : '';
        return `${etapa}: ${msg || 'erro desconhecido'}${codigo}${detalhe}${dica}`;
    };

    /** Desfaz a negociação recém-criada quando uma etapa seguinte falha. Se o
     *  próprio DELETE falhar, "nenhum dado foi salvo" viraria mentira e a
     *  negociação ficaria órfã no funil — devolve o texto que corresponde ao
     *  que de fato aconteceu. */
    const desfazerNegociacao = async (id: number | string): Promise<string> => {
        const { error } = await supabase.from('negociacoes').delete().eq('id', id);
        if (error) {
            console.error('Rollback da negociação falhou', error);
            return ` ATENÇÃO: a negociação #${id} ficou criada pela metade e precisa ser excluída manualmente.`;
        }
        return ' Nenhum dado foi salvo.';
    };

    /** Itens sem os quais a negociação não pode nascer: NOT NULL no banco, regra
     *  comercial, ou dado que o wizard depende de ter carregado. Devolve o rótulo
     *  exato que aparece no formulário, para o vendedor achar o campo. */
    const camposObrigatoriosFaltando = (): string[] => {
        const faltando: string[] = [];
        if (!basicInfo.nome_cliente?.trim()) faltando.push(tipoCliente === 'PF' ? 'Nome Completo' : 'Razão Social');
        if (!basicInfo.whatsapp?.trim()) faltando.push('WhatsApp');
        if (isVendedorInterno && !basicInfo.origem) faltando.push('Origem');
        // stage é NOT NULL e vem do funil; vazio significa que os estágios não carregaram.
        if (!basicInfo.stage?.trim()) faltando.push('Etapa (Stage) — o funil de vendas não carregou, reabra a tela');
        if (!selectedPipelineId) faltando.push('Funil de destino — não carregou, reabra a tela');
        if (basicInfo.associateUC) {
            if (ucs.length === 0) {
                faltando.push('Nenhuma UC adicionada (a opção "associar Unidade Consumidora" está ligada)');
            }
            ucs.forEach((uc, i) => {
                if (!uc.numero_uc?.trim()) faltando.push(`Número da UC (unidade ${i + 1} da lista)`);
            });
        }
        return faltando;
    };

    const handleCreateNegotiation = async () => {
        console.log('>>> STARTING CREATE NEGOTIATION PROCESS');

        const faltando = camposObrigatoriosFaltando();
        if (faltando.length > 0) {
            addToast(
                `Não foi possível criar a negociação. ${faltando.length === 1 ? 'Falta preencher' : 'Faltam preencher'}: ${faltando.join('; ')}.`,
                'error'
            );
            return;
        }

        setLoading(true);
        try {
            let userId: string | undefined;
            let userProfile: any = null;
            try {
                const { data: { user }, error: authError } = await supabase.auth.getUser();
                if (authError) throw authError;
                userId = user?.id;
                if (userId) {
                    const { data: profileData, error: profileError } = await supabase.from('profiles').select('*').eq('id', userId).single();
                    if (profileError) throw profileError;
                    userProfile = profileData;
                }
            } catch (authError) {
                console.error('Auth/Profile Error:', authError);
                // Antes seguia em frente: sem userId a negociação nascia com
                // responsavel_id nulo — órfã, fora do funil de quem a criou.
                addToast(descreverFalha('Não foi possível confirmar seu usuário', authError), 'error');
                setLoading(false);
                return;
            }

            if (!userId) {
                addToast('Sua sessão expirou. Saia e entre de novo no sistema para criar a negociação.', 'error');
                setLoading(false);
                return;
            }
            // Perfil é o que carimba plano_modalidade; o do contexto serve de reserva.
            userProfile = userProfile ?? profile;

            // Funil de destino: sem ele a negociação nasce com pipeline_id nulo e,
            // por causa do `pipeline_id.is.null` do quadro, passa a aparecer em TODOS
            // os funis ao mesmo tempo. Melhor falhar do que gravar isso.
            let pipelineDestino = selectedPipelineId;
            if (!pipelineDestino) {
                pipelineDestino = await buscarFunilDeVendasId();
            }
            if (!pipelineDestino) {
                addToast('Não foi possível identificar o funil de destino. Recarregue a página e tente novamente.', 'error');
                setLoading(false);
                return;
            }

            // Snapshot da condição comercial (mesmo cálculo do modal "Alterar Plano") —
            // sem isso a negociação nasce sem plano_codigo/publico/multiplicadores,
            // e a régua de aprovação de desconto/carência não tem como avaliar a condição.
            const volumeTotalUCs = ucs.reduce((s, uc) => s + (Number(uc.consumo_medio_kwh) || 0), 0);
            const planoResultado = planDetails.plano_codigo
                ? calcularMultiplicadorFinal(planDetails.plano_codigo, volumeTotalUCs, planDetails.fidelidade)
                : null;

            // Outra pessoa paga o boleto: o pagador sai antes da negociação, para
            // ela e as UCs já nascerem com ele (o gatilho da UC herda da negociação).
            let pagadorId: string | null = null;
            if (basicInfo.pagador_mesmo === false) {
                try {
                    const r = await resolverPagador({
                        nome: basicInfo.pagador_nome,
                        cpf_cnpj: basicInfo.pagador_cpf_cnpj,
                        email: basicInfo.pagador_email,
                        whatsapp: basicInfo.pagador_whatsapp,
                    });
                    pagadorId = r.pagador.id;
                    if (r.nomeDiferente) {
                        addToast(`Este CPF/CNPJ já é do pagador "${r.pagador.nome}". O boleto vai sair nesse nome.`, 'warning', 12000);
                    }
                } catch (e: any) {
                    addToast(descreverFalha('Não foi possível salvar o pagador', e) + ' A negociação não foi criada.', 'error');
                    setLoading(false);
                    return;
                }
            }

            // Create Negotiation
            let negData: any = null;
            try {
                const { data, error } = await supabase
                    .from('negociacoes')
                    .insert([{
                        titulo: basicInfo.nome_cliente,
                        cliente_nome: basicInfo.nome_cliente,
                        stage: basicInfo.stage,
                        status: basicInfo.status,
                        consumo_medio_kwh: 0,
                        tipo_cliente: tipoCliente,
                        cpf_cnpj: basicInfo.cpf_cnpj.replace(/\D/g, ''),
                        whatsapp: basicInfo.whatsapp,
                        email: basicInfo.email,
                        origem: basicInfo.origem || null,
                        plataforma: basicInfo.plataforma || null,
                        campanha: basicInfo.campanha || null,
                        anuncio: basicInfo.anuncio || null,
                        formulario: basicInfo.formulario || null,
                        reside_goias: basicInfo.reside_goias ?? null,
                        numero_uc: ucs.length > 0 ? ucs[0].numero_uc : null,
                        // GeoIntelligence: localização e segmento herdados da primeira UC
                        segmento: ucs.length > 0 ? (ucs[0].segmento || null) : null,
                        cidade: ucs.length > 0 ? (ucs[0].cidade || null) : null,
                        bairro: ucs.length > 0 ? (ucs[0].bairro || null) : null,
                        responsavel_id: userId,
                        // Congela o modelo de plano pelo cargo de quem está criando (ou o que a
                        // gerente escolheu) — transferir o responsável depois não muda o formulário.
                        plano_modalidade: podeEscolherModalidade ? modalidadeNaCriacao : modalidadePorPerfil(userProfile),
                        fidelidade: planDetails.fidelidade,
                        desconto_adicional: planDetails.desconto_adicional,
                        plano_codigo: planDetails.plano_codigo || null,
                        publico: planoResultado?.publico ?? null,
                        multiplicador_plano: planoResultado?.multPlano ?? null,
                        fator_fidelidade: planoResultado?.fatorFidelidade ?? null,
                        multiplicador_final: planoResultado?.final ?? null,
                        pipeline_id: pipelineDestino,
                        cpf_representante: tipoCliente === 'PJ' ? basicInfo.cpf_representante.replace(/\D/g, '') : null,
                        nome_representante_legal: tipoCliente === 'PJ' ? basicInfo.nome_representante_legal : null,
                        data_nascimento_representante: tipoCliente === 'PJ' ? (basicInfo.data_nascimento_representante || null) : null,
                        contato_financeiro: basicInfo.contato_financeiro || null,
                        email_financeiro: basicInfo.email_financeiro || null,
                        whatsapp_financeiro: basicInfo.whatsapp_financeiro || null,
                        contrato_social_data_versao: tipoCliente === 'PJ' ? (basicInfo.contrato_social_data_versao || null) : null,
                        ...(basicInfo.pagador_mesmo === null ? {} : { pagador_e_assinante: basicInfo.pagador_mesmo }),
                        ...(pagadorId ? { pagador_id: pagadorId } : {}),
                    }])
                    .select()
                    .single();

                if (error) throw error;
                negData = data;
                console.log('Negotiation created:', negData.id);
                logUsageEvent({ userId, modulo: 'crm', funcionalidade: 'funil', acao: 'criar_negociacao', origem: 'acao' });
            } catch (dbError: any) {
                console.error('Database Error (Insert Negotiation):', dbError);
                // 23505 = violação de UNIQUE; 42501 = RLS barrou o INSERT. Os dois
                // têm causa acionável e mereciam nome, em vez da mensagem crua.
                const especifico =
                    dbError?.code === '42501'
                        ? 'Seu usuário não tem permissão para criar negociações. Peça ao administrador para revisar seu cargo no sistema.'
                        : dbError?.code === '23505'
                            ? 'Já existe uma negociação com estes dados (registro duplicado). Verifique se o cliente já não foi cadastrado.'
                            : descreverFalha('Erro ao salvar a negociação', dbError);
                addToast(`${especifico} A negociação não foi criada.`, 'error');
                setLoading(false);
                return;
            }

            // Insert UCs
            if (ucs.length > 0) {
                // UC marcada como "troca de dono" já existe: não é inserida, vira
                // pedido de troca depois das UCs novas (ver solicitarTrocaDonoUc).
                const ucsNovas = (ucs as UCData[]).filter(uc => !uc.troca_dono);
                const ucsTroca = (ucs as UCData[]).filter(uc => uc.troca_dono);

                // Validate UC uniqueness before inserting
                const ucNumbers = ucsNovas.map(uc => uc.numero_uc.trim()).filter(Boolean);

                // Duplicata DENTRO do próprio lote: o INSERT é atômico e a trava de UC
                // duplicada descartaria todas as UCs, deixando a negociação órfã.
                // Compara "Número da UC" e "Outro Nº UC" juntos e sem máscara.
                const dupInBatch = numeroRepetidoNoLote(ucs);
                if (dupInBatch) {
                    const desfeita = await desfazerNegociacao(negData.id);
                    addToast(`A UC ${dupInBatch} foi adicionada mais de uma vez. Remova a duplicata e tente novamente.${desfeita}`, 'error');
                    setLoading(false);
                    return;
                }

                if (ucNumbers.length > 0) {
                    // Cruza "Número da UC" e "Outro Nº UC", sem máscara: comparar só
                    // numero_uc pelo texto deixava passar a mesma UC trocada ou formatada.
                    let checkError: unknown = null;
                    let duplicatas: string[] = [];
                    try {
                        const achadas = await Promise.all(
                            ucsNovas.map(uc => buscarUcComMesmoNumero(supabase, uc.numero_uc, uc.outro_numero_uc))
                        );
                        duplicatas = ucsNovas.filter((_, i) => achadas[i]).map(uc => uc.numero_uc.trim());
                    } catch (err) {
                        checkError = err;
                    }

                    // Sem esta checagem o fluxo seguia às cegas e a duplicata só
                    // aparecia como 23505 lá na frente, sem dizer qual UC era.
                    if (checkError) {
                        console.error('Error checking existing UCs', checkError);
                        const desfeita = await desfazerNegociacao(negData.id);
                        addToast(
                            descreverFalha('Não foi possível verificar se as UCs já estão cadastradas', checkError) + desfeita,
                            'error'
                        );
                        setLoading(false);
                        return;
                    }

                    if (duplicatas.length > 0) {
                        // Rollback: delete the negotiation we just created
                        const desfeita = await desfazerNegociacao(negData.id);
                        addToast(
                            `${duplicatas.length === 1 ? 'A UC a seguir já está cadastrada' : 'As UCs a seguir já estão cadastradas'} no sistema: ${duplicatas.join(', ')}.${desfeita}`,
                            'error'
                        );
                        setLoading(false);
                        return;
                    }
                }

                const ucsToInsert = ucsNovas.map(uc => ({
                    negociacao_id: negData.id,
                    numero_uc: uc.numero_uc,
                    nome_cliente: basicInfo.nome_cliente,
                    situacao: null, // UC em negociação ainda não tem situação operacional (check constraint só aceita Ativa/Ciclos/Cancelada)
                    status_financeiro: 'Não se aplica',
                    cep: uc.cep,
                    logradouro: uc.logradouro,
                    numero: uc.numero,
                    complemento: uc.complemento,
                    quadra: uc.quadra || null,
                    lote: uc.lote || null,
                    bairro: uc.bairro,
                    cidade: uc.cidade,
                    estado: uc.estado,
                    endereco: `${uc.logradouro}, ${uc.numero} - ${uc.bairro}, ${uc.cidade}/${uc.estado}`,
                    tipo_ligacao: uc.tipo_ligacao || null,
                    grupo_tarifario: uc.grupo_tarifario || null,
                    concessionaria: uc.concessionaria || null,
                    consumo_medio_kwh: uc.consumo_medio_kwh || 0,
                    valor_tarifa: uc.valor_tarifa || null,
                    desconto_tarifa: (planDetails.desconto_tarifa / 100),
                    desconto_bandeira: (planDetails.desconto_bandeira / 100),
                    carencia: planDetails.carencia,
                    titular_fatura: uc.titular_fatura || null,
                    cpf_cnpj_titular: uc.cpf_cnpj_titular || null,
                    data_nascimento_titular: uc.data_nascimento_titular || null,
                    whatsapp_cliente: basicInfo.whatsapp || null,
                    email_cliente: basicInfo.email || null,
                    observacoes_uc: uc.observacoes_uc || null,
                    cpf_cnpj_cliente: basicInfo.cpf_cnpj ? basicInfo.cpf_cnpj.replace(/\D/g, '') : null,
                    num_medidor: uc.num_medidor || null,
                    outro_numero_uc: uc.outro_numero_uc || null,
                    segmento: uc.segmento || null
                }));

                const { error: ucError } = ucsToInsert.length > 0
                    ? await supabase.from('unidades_consumidoras').insert(ucsToInsert)
                    : { error: null };

                if (ucError) {
                    console.error('Error creating UCs', ucError);
                    // Rollback: nunca deixar a negociação órfã (com numero_uc mas sem UCs).
                    const desfeita = await desfazerNegociacao(negData.id);
                    const motivo =
                        ucError.code === '23505'
                            ? mensagemUcDuplicada(ucError) ?? 'Uma das UCs informadas já está cadastrada (número duplicado).'
                            : ucError.code === '23502'
                                ? 'Falta um campo obrigatório da UC (número da UC ou nome do cliente).'
                                : ucError.code === '42501'
                                    ? 'Seu usuário não tem permissão para cadastrar Unidades Consumidoras.'
                                    : ucError.code === '23514'
                                        ? 'Algum campo da UC está fora dos valores aceitos pelo sistema (situação, grupo tarifário ou segmento).'
                                        : descreverFalha('Erro ao salvar as UCs', ucError);
                    addToast(`${motivo}${desfeita}`, 'error');
                    setLoading(false);
                    return;
                }

                // Create feed notes from UC observations
                const ucNotes = ucsNovas
                    .filter(uc => uc.observacoes_uc && uc.observacoes_uc.trim())
                    .map(uc => ({
                        negociacao_id: negData.id,
                        user_id: userId,
                        conteudo: `[UC ${uc.numero_uc}] ${uc.observacoes_uc}`,
                        tipo: 'nota'
                    }));

                if (ucNotes.length > 0) {
                    // Não desfaz a negociação: a observação da UC é acessório, mas
                    // some em silêncio se ninguém avisar que não foi gravada.
                    const { error: notasError } = await supabase.from('negociacao_notas').insert(ucNotes);
                    if (notasError) {
                        console.error('Error creating UC notes', notasError);
                        addToast(
                            descreverFalha('Negociação criada, mas as observações da UC não foram salvas no feed', notasError),
                            'error'
                        );
                    }
                }

                // Troca de dono: a UC passa a aparecer nesta negociação, mas segue
                // cobrada do dono atual até a análise daqui ser aprovada. Falhou uma,
                // desfaz tudo — excluir a negociação devolve as UCs já pedidas
                // (trigger trg_troca_dono_antes_excluir_negociacao).
                for (const uc of ucsTroca) {
                    try {
                        await solicitarTrocaDonoUc(supabase, {
                            negociacaoDestinoId: negData.id,
                            ucId: uc.troca_dono!.uc_id,
                            justificativa: uc.troca_dono!.justificativa,
                            descontoTarifa: planDetails.desconto_tarifa / 100,
                            descontoBandeira: planDetails.desconto_bandeira / 100,
                            carencia: planDetails.carencia,
                        });
                    } catch (trocaError) {
                        console.error('Error requesting UC owner change', trocaError);
                        const desfeita = await desfazerNegociacao(negData.id);
                        addToast(descreverFalha(`Não foi possível pedir a troca de dono da UC ${uc.numero_uc}`, trocaError) + desfeita, 'error');
                        setLoading(false);
                        return;
                    }
                }
            }

            // Anexa documento + todas as faturas em UMA ÚNICA chamada. Fazer isso em chamadas separadas
            // gera condição de corrida: cada uma lê/regrava o array documentos_json inteiro, e quem
            // terminar por último no n8n apaga o que as outras já tinham salvo.
            const arquivosParaAnexar: { file: File; customName: string }[] = [];
            if (prefillData?.documentoFile) {
                arquivosParaAnexar.push({
                    file: prefillData.documentoFile,
                    customName: prefillData.documentoLabel || prefillData.documentoFile.name.replace(/\.(pdf|jpe?g|png|heic|webp)$/i, ''),
                });
            }
            if (prefillData?.faturaFiles) {
                for (const item of prefillData.faturaFiles) {
                    arquivosParaAnexar.push({
                        file: item.file,
                        customName: item.label || item.file.name.replace(/\.pdf$/i, ''),
                    });
                }
            }
            if (arquivosParaAnexar.length > 0) {
                const result = await uploadDocumentsToNegotiation(negData.id, arquivosParaAnexar);
                if (!result.ok) {
                    console.error('[Automático] Falha ao anexar arquivos aos documentos:', result.error);
                    addToast(
                        `Negociação criada, mas ${arquivosParaAnexar.length === 1 ? 'o arquivo não foi anexado' : `os ${arquivosParaAnexar.length} arquivos não foram anexados`} (documento/fatura): ${result.error || 'erro desconhecido'}. Anexe manualmente na negociação.`,
                        'error'
                    );
                }
            }

            // Send to Webhook
            console.log('--- STARTING WEBHOOK SEND ---');

            // Directly use the webhook URL (CORS should be handled by n8n or environment)
            const targetUrl = webhookUrl;
            console.log('Webhook URL (Original):', webhookUrl);
            console.log('Webhook URL (Target):', targetUrl);

            if (targetUrl) {
                const formData = new FormData();
                formData.append('negociacao_id', negData.id);
                formData.append('titulo', negData.titulo);
                formData.append('cliente_nome', basicInfo.nome_cliente);
                formData.append('cliente_nome_arquivo', sanitizeNomeParaArquivo(basicInfo.nome_cliente));
                formData.append('stage', basicInfo.stage || '');
                formData.append('pipeline_id', pipelineDestino);
                formData.append('responsavel_id', userId || '');
                formData.append('whatsapp', basicInfo.whatsapp);
                formData.append('desconto_adicional', planDetails.desconto_adicional || '');

                if (tipoCliente === 'PJ') {
                    formData.append('cpf_representante', basicInfo.cpf_representante.replace(/\D/g, ''));
                    formData.append('nome_representante_legal', basicInfo.nome_representante_legal);
                    formData.append('representante_legal', basicInfo.nome_representante_legal);
                    formData.append('nome_representante', basicInfo.nome_representante_legal);
                }

                Object.entries(documentFiles).forEach(([key, file]) => {
                    if (Array.isArray(file)) {
                        file.forEach((f, index) => {
                            if (f instanceof File) formData.append(`${key}_${index + 1}`, f);
                        });
                    } else if (file instanceof File) {
                        formData.append(key, file);
                    }
                });

                try {
                    console.log('Sending FormData to Webhook...', formData);
                    const response = await fetch(targetUrl, {
                        method: 'POST',
                        body: formData
                    });
                    const responseText = await response.text();
                    console.log('Webhook Response Status:', response.status);
                    console.log('Webhook Response Body:', responseText);

                    if (!response.ok) {
                        console.error('Webhook Failed:', responseText);
                        // Aviso, não erro: a negociação já está salva. Mas quem
                        // depende do termo/pasta gerados pelo n8n precisa saber
                        // que a automação não rodou, em vez de esperar em vão.
                        addToast(
                            `Negociação criada, mas a automação (n8n) recusou o envio (HTTP ${response.status}). O termo/pasta pode não ter sido gerado — avise o TI.`,
                            'warning'
                        );
                    }
                } catch (webError) {
                    console.error('Error sending to webhook:', webError);
                    addToast(
                        descreverFalha('Negociação criada, mas não foi possível acionar a automação (n8n)', webError) +
                        ' O termo/pasta pode não ter sido gerado.',
                        'warning'
                    );
                }
            } else {
                console.warn('No Webhook URL configured.');
                addToast(
                    'Negociação criada, mas o endereço da automação (n8n) não está configurado no sistema. O termo/pasta não será gerado — avise o TI.',
                    'warning'
                );
            }

            // Logs
            try {
                await logNegotiationAction(negData.id, userId, 'criou o negócio');

                // --- APPROVAL LOGIC (dentro/fora da política + carência) ---
                await checkAndRequestDiscountApproval(
                    negData.id,
                    userId,
                    planDetails.desconto_tarifa,
                    planDetails.desconto_bandeira,
                    planDetails.fidelidade,
                    {
                        planoCodigo: planDetails.plano_codigo,
                        carencia: planDetails.carencia,
                        fidelidadeIsenta: isFidelidadeIsenta(planDetails.fidelidade),
                    }
                );

            } catch (logError) {
                console.error('Error logging action:', logError);
                // Não desfaz a negociação, mas não pode passar em silêncio: sem a
                // régua de aprovação a negociação fica sem desconto_status, e o
                // gerente nunca recebe o pedido de aprovação para avaliar.
                addToast(
                    descreverFalha('Negociação criada, mas o histórico/aprovação de desconto não foi registrado', logError) +
                    ' Confira a negociação antes de enviar o termo.',
                    'warning'
                );
            }

            // pipeline_id do servidor: cobre trigger que porventura mova o funil.
            onSuccess(negData.id, negData.pipeline_id ?? pipelineDestino);
            onClose();
        } catch (error) {
            console.error('Error creating negotiation:', error);
            // Rede de segurança: cada etapa acima já trata o próprio erro, então
            // cair aqui significa falha inesperada fora delas.
            addToast(descreverFalha('Erro inesperado ao criar a negociação', error), 'error');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in">
            <div className={`bg-white dark:bg-slate-900 rounded-2xl w-full max-w-4xl shadow-2xl overflow-hidden transform scale-100 transition-all flex flex-col max-h-[90vh]`}>
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700/50 flex justify-between items-center bg-gradient-to-r from-primary/5 dark:from-primary/10 to-transparent">
                    <div>
                        <h3 className="font-poppins font-bold text-gray-800 dark:text-slate-100 text-lg">Nova Negociação</h3>
                        <p className="text-xs text-gray-500 dark:text-slate-400">Etapa {currentStepIndex + 1} de {totalSteps}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white dark:hover:bg-slate-800 rounded-full transition-colors text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Progress Bar */}
                <div className="h-1 w-full bg-gray-100 dark:bg-slate-800">
                    <div
                        className="h-full bg-primary transition-all duration-300"
                        style={{ width: `${((currentStepIndex + 1) / totalSteps) * 100}%` }}
                    />
                </div>

                {/* Body - Dynamic Content */}
                <div className="p-6 overflow-y-auto">

                    {/* Seletor de funil — por ETAPA NOMEADA, não por índice: o fluxo
                        "Automático" começa no índice 1 (pula a escolha PF/PJ), então a
                        condição antiga `currentStepIndex === 0` escondia o seletor
                        justamente de quem cria a negociação lendo a fatura. */}
                    {(currentStepString === 'client_type' || currentStepString === 'basic_info') && (
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Funil de destino</label>
                            <div className="relative">
                                <select
                                    className="w-full p-3 bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none appearance-none text-gray-800 dark:text-slate-100"
                                    value={selectedPipelineId || ''}
                                    onChange={(e) => setSelectedPipelineId(e.target.value)}
                                >
                                    {pipelines.map(p => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                </select>
                            </div>
                            {/* Trocar o funil dispara fetchPipelineStages, que SOBRESCREVE
                                basicInfo.stage com a primeira etapa do novo funil — correto,
                                já que etapa de um funil não existe no outro. */}
                            <p className="mt-1.5 text-xs text-gray-500 dark:text-slate-400">
                                Venda nova entra no Funil de Vendas. Trocar o funil redefine a etapa inicial.
                            </p>
                        </div>
                    )}

                    {currentStepString === 'client_type' && (
                        <StepClientType
                            tipo={tipoCliente}
                            onChange={setTipoCliente}
                            onNext={handleNext}
                        />
                    )}

                    {currentStepString === 'basic_info' && (
                        <StepBasicInfo
                            data={basicInfo}
                            pipelineStages={pipelineStages}
                            tipo={tipoCliente}
                            origemObrigatoria={!!isVendedorInterno}
                            onChange={(field, value) => {
                                setBasicInfo(prev => ({ ...prev, [field]: value }));
                            }}
                            onNext={handleNext}
                            onBack={handleBack}
                            onFinish={handleCreateNegotiation}
                            loading={loading}
                        />
                    )}

                    {currentStepString === 'uc' && (
                        <StepUC
                            ucs={ucs}
                            onAddUC={(uc) => setUcs([...ucs, uc])}
                            onRemoveUC={(id) => setUcs(ucs.filter(u => u.id !== id))}
                            onBack={handleBack}
                            onFinish={() => {
                                handleNext();
                            }}
                            loading={loading}
                            wantPlan={wantPlan}
                            setWantPlan={setWantPlan}
                            hasNextStep={currentStepIndex < totalSteps - 1} // Should be true if wantPlan or documents
                            cliente_nome={basicInfo.nome_cliente}
                            cliente_cpf_cnpj={basicInfo.cpf_cnpj}
                            tipoCliente={tipoCliente}
                            representante={{
                                nome: basicInfo.nome_representante_legal,
                                cpf: basicInfo.cpf_representante,
                                data_nascimento: basicInfo.data_nascimento_representante,
                            }}
                            onChangeRepresentante={(field, value) => setBasicInfo(prev => ({
                                ...prev,
                                ...(field === 'nome' ? { nome_representante_legal: value } : {}),
                                ...(field === 'cpf' ? { cpf_representante: value } : {}),
                                ...(field === 'data_nascimento' ? { data_nascimento_representante: value } : {}),
                            }))}
                        />
                    )}

                    {currentStepString === 'plan' && (
                        <StepPlan
                            data={planDetails}
                            onChange={(field, value) => setPlanDetails(prev => ({ ...prev, [field]: value }))}
                            onBack={handleBack}
                            onFinish={handleNext}
                            loading={loading}
                            hasNextStep={currentStepIndex < totalSteps - 1}
                            volumeKwh={ucs.reduce((s, uc) => s + (Number(uc.consumo_medio_kwh) || 0), 0)}
                            modalidade={modalidadeNaCriacao}
                            onChangeModalidade={podeEscolherModalidade ? setModalidadeEscolhida : undefined}
                        />
                    )}

                    {currentStepString === 'documents' && (
                        <StepDocuments
                            files={documentFiles}
                            onFileChange={(field, file) => setDocumentFiles(prev => ({ ...prev, [field]: file }))}
                            onBack={handleBack}
                            onFinish={handleCreateNegotiation}
                            loading={loading}
                        />
                    )}

                </div>
            </div>
        </div>,
        document.body
    );
};

export default NegotiationWizard;
